import {
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  permissionConfig,
  PermissionTypes,
  UserTypes,
} from 'shared';
import { When } from 'react-if';
import { DateTime } from 'luxon';
import { CancelRounded } from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';

import { Card } from '../../../../common/Atoms/Card';
import { MultiButtonCardHeader } from '../../../../common/Atoms/MultiButtonCardHeader';
import { DescriptionList } from '../../../../common/Atoms/DescriptionList';
import { Spinner } from '../../Common/Spinner';
import { useGetSelectedOrganisation } from '../../../hooks/useGetSelectedOrganisation';
import { useInviteSupportMutation, useRemoveSupportMutation } from '../../../services/api/organisationApi/organisation';
import { useCancelInviteMutation, useUninviteMutation } from '../../../services/api/userApi/user';
import { Paragraph } from '../../../../common/Atoms/Typography/Paragraph';
import { useUser } from '../../../hooks/useUser';
import { usePlanDetails } from '../../../hooks/usePlanDetails';
import { classNames } from '../../../../common/lib/classNames';
import { Button } from '../../../../common/Atoms/Button';
import { LeaveConfirmationModal } from '../../Modals/LeaveConfirmationModal';

import { PermissionModal } from './PermissionModal';
import { InviteModal } from './InviteModal';

export function UserSettings() {
  const [ removeSupport, { isLoading: isRemovingSupport } ] = useRemoveSupportMutation();
  const [ inviteSupport, { isLoading: isInvitingSupport } ] = useInviteSupportMutation();

  const currentOrg = useGetSelectedOrganisation();
  const usage = usePlanDetails();
  const { data: user } = useUser();
  const userConnections = currentOrg?.users || [];

  const [ uninvite, { isLoading: isUninviting } ] = useUninviteMutation();
  const [ cancelInvite, { isLoading: isCancellingInvite } ] = useCancelInviteMutation();
  const [permissionModal, setPermissionModal] = useState<typeof userConnections[number] | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [leaveOrgModalOpen, setLeaveOrgModalOpen] = useState(false);

  const connectedUser = useMemo(() => {
    return (user?.orgs?.find(org => org.id === currentOrg.id)?.type === UserTypes.Inherit) || false;
  }, [user, currentOrg]);

  function onCloseInviteModal() {
    setShowInviteModal(false);
  }

  function onRemoveUser(email: string) {
    uninvite({ orgId: currentOrg.id, userEmailBeingUninvited: email });
  }

  function onCancelInvite(id: number) {
    cancelInvite(id);
  }

  function onEditPermissions(connectedUser) {
    setPermissionModal(connectedUser);
  }

  // Keep state in permission modal in sync
  useEffect(() => {
    if (permissionModal) {
      const currentModalUser = userConnections?.find(c => c.id === permissionModal.id);

      if (currentModalUser && permissionModal.permissionSettings !== currentModalUser.permissionSettings) {
        setPermissionModal(currentModalUser);
      }
    }
  }, [permissionModal, userConnections]);

  const connections = useMemo(() => {
    return userConnections.map(c => {
      const isCurrentUser = c.id === user?.id;
      // TODO: this is a bit janky, we need consistency in users but for now this will allow all users to be displayed
      let connected = null;
      let actions = (<span></span>);
      if (c.type === UserTypes.Support) {
        connected = (<Paragraph as={ `span` }>{ `Paidnice Support` }</Paragraph>);
      }
      else if (c.type === UserTypes.Invited) {
        connected = (
          <span className={ `flex flex-col` }>
            <Paragraph as={ `span` }>{ `Invited` }</Paragraph>
            <Tooltip title={ permissionConfig[PermissionTypes.SETTINGS].levels[c.permissionSettings]?.description }>
              <span>
                <Paragraph
                  as={ `span` }
                  variant={ `help` }
                  className={ `font-semibold` }
                >
                  { permissionConfig[PermissionTypes.SETTINGS].title }
                  { `: ` }
                </Paragraph>

                <Paragraph
                  as={ `span` }
                  variant={ `help` }
                >
                  { c.permissionSettings }
                </Paragraph>
              </span>
            </Tooltip>
          </span>
        );

        if (connectedUser) {
          // Connected user means inherit, and can therefor modify permissions
          actions = (
            <span className={ `flex flex-col items-end` }>
              <a
                href={ `#` }
                onClick={ e => {
                  e.preventDefault();
                  onEditPermissions(c);
                } }
              >
                <Paragraph
                  as={ `span` }
                  variant={ `link` }
                >
                  { `Edit Permissions` }
                </Paragraph>
              </a>

              <a
                href={ `#` }
                onClick={ e => {
                  e.preventDefault();
                  if (!isUninviting) {
                    onRemoveUser(c.email);
                  }
                } }
              >
                <Paragraph
                  as={ `span` }
                  variant={ `link` }
                >
                  { isUninviting ? `Please Wait..` : `Remove User` }
                </Paragraph>
              </a>
            </span>
          );
        }

        // This is the row for the current logged in user, and they are of the invited type. Allow them to leave the org
        if (isCurrentUser) {
          actions = (
            <span className={ `flex flex-col items-end` }>
              <Button
                color={ `red` }
                onClick={ () => setLeaveOrgModalOpen(true) }
              >
                { `Leave Organisation` }
              </Button>
            </span>
          );
        }
      }
      else {
        connected = (<Paragraph as={ `span` }>{ `${connected = c.connected ? `Connected` : `Disconnected`}` }</Paragraph>);
      }

      return ({
        title: c.firstName? (c.firstName + ` ` + c.lastName) : c.email,
        content: connected,
        child: actions,
        subText: c.lastLogin && `Last Login: ${DateTime.fromJSDate(new Date(c.lastLogin)).toFormat(`dd LLL yyyy HH:mm`)}`,
      });
    });
  }, [ userConnections, user ]);

  const hasSupport = userConnections?.some(c => c.type === UserTypes.Support);

  const maxUsers = (usage?.users?.current ?? 0) >= (usage?.users?.max ?? Infinity);

  const buttonArray = useMemo(() => {
    const buttons: {
      buttonText: string;
      onClick: () => void;
      loading?: boolean;
      disabled?: boolean;
    }[] = hasSupport ?
      [{
        buttonText: `Remove Paidnice Support`,
        onClick: handleRemoveSupport,
        loading: isRemovingSupport,
      }] :
      [{
        buttonText: `Invite Paidnice Support`,
        onClick: handleConnectSupport,
        loading: isInvitingSupport,
      }];

    if (connectedUser) {
      buttons.unshift({
        buttonText: `Invite User`,
        onClick: () => { setShowInviteModal(true); },
      });
    }

    return buttons;
  }, [isInvitingSupport, isRemovingSupport, maxUsers, connectedUser, hasSupport]);

  function handleConnectSupport() {
    return inviteSupport(currentOrg.id);
  }

  function handleRemoveSupport() {
    return removeSupport(currentOrg.id);
  }

  const usersHeader = (
    <div className={ `flex justify-between px-4 py-5 sm:px-6` }>
      <Paragraph
        variant={ `secondary` }
        className={ `mt-1 max-w-2xl` }
        as={ `div` }
      >
        { `Users that have access to this organisation` }
      </Paragraph>

      { /* User Usage */ }
      <When condition={ !!usage }>
        { () => (
          <div>
            <div className={ `flex space-x-1 justify-end` }>
              <Paragraph
                className={ classNames(
                  maxUsers ? `text-red-500` : ``,
                ) }
              >
                { usage.users?.current }
              </Paragraph>
              <Paragraph>
                { `/` }
              </Paragraph>
              <Paragraph>
                { usage.users?.max }
              </Paragraph>
              <Paragraph>
                { `users` }
              </Paragraph>
            </div>

            <When condition={ maxUsers }>
              <Paragraph
                variant={ `help` }
              >
                { `Your plan includes ${usage.users?.max} users` }
              </Paragraph>
            </When>
          </div>
        ) }
      </When>
    </div>
  );

  if (!currentOrg) {
    return <Spinner />;
  }

  return (
    <div>
      <PermissionModal
        setOpen={ () => setPermissionModal(null) }
        open={ !!permissionModal }
        userConnection={ permissionModal }
      />
      <InviteModal
        open={ showInviteModal }
        setOpen={ onCloseInviteModal }
      />
      <LeaveConfirmationModal
        open={ leaveOrgModalOpen }
        setOpen={ () => setLeaveOrgModalOpen(false) }
        orgId={ currentOrg.id }
        orgName={ currentOrg.legalName }
      />

      <Card>
        <MultiButtonCardHeader
          title={ `Users` }
          buttons={ buttonArray }
          noDivider={ true }
        />
        <DescriptionList
          items={ connections }
          header={ usersHeader }
        />

        <When condition={ currentOrg.pendingInvites?.length > 0 }>
          <div className={ `py-4` } />

          <DescriptionList
            className={ `mt-3` }
            description={ `Pending Invites` }
            items={ currentOrg.pendingInvites?.map(invite => ({
              title: invite.name || `No name`,
              content: (
                <span className={ `flex flex-col` }>
                  <Paragraph as={ `span` }>{ invite.email }</Paragraph>
                  <When condition={ invite.defaultPermissions && invite.defaultPermissions?.[PermissionTypes.SETTINGS] }>
                    { () => (
                      <Tooltip title={ permissionConfig[PermissionTypes.SETTINGS].levels[invite.defaultPermissions?.settings]?.description }>
                        <span>
                          <Paragraph
                            as={ `span` }
                            variant={ `help` }
                            className={ `font-semibold` }
                          >
                            { permissionConfig[PermissionTypes.SETTINGS].title }
                            { `: ` }
                          </Paragraph>

                          <Paragraph
                            as={ `span` }
                            variant={ `help` }
                          >
                            { invite.defaultPermissions?.settings }
                          </Paragraph>
                        </span>
                      </Tooltip>
                    ) }
                  </When>
                </span>
              ),
              child: (
                <div>
                  <Paragraph
                    as={ `span` }
                    variant={ `help` }
                  >
                    { `Invite Sent: ${DateTime.fromJSDate(new Date(invite.createdAt)).toFormat(`dd LLL yyyy HH:mm`)}` }
                  </Paragraph>
                  { /* When user is inherit type or they authored it, they are allowed to cancel invites */ }
                  <When condition={ !!connectedUser || invite.invitedBy === user?.email }>
                    <Tooltip
                      title={ `Cancel Invite` }
                    >
                      <IconButton
                        sx={ {
                          padding: 0,
                          marginLeft: `8px`,
                        } }
                        onClick={ () => onCancelInvite(invite.id) }
                        loading={ isCancellingInvite }
                      >
                        <CancelRounded />
                      </IconButton>
                    </Tooltip>
                  </When>
                </div>
              ),
            })) || [] }
          />
        </When>
      </Card>
    </div>
  );
}
